// GraphQL operations for the agent course-generation flow (task 16.5): submit a
// topic prompt so the Agent_Service researches it and proposes a course
// structure, poll that request until the proposal is READY, then approve the
// (possibly edited) structure to trigger script generation — or reject it and
// request a fresh proposal.
//
// Each frontend feature owns its own operations file. This file is dedicated to
// the agent flow so it stays decoupled from graphql/courses.ts (manual course
// create + Course detail) and graphql/profiles.ts (profile CRUD). It declares
// its OWN Project.voiceProfiles / Project.artStyleProfiles selector document so
// the agent form can offer the project's Voice_Profile and Art_Style_Profile
// selectors without importing another feature's document (no cross-coupling).
//
// Requirements 5.2 (present the proposed structure for review), 5.3 (accept an
// edited structure whose episode count stays within 3–12), and 5.5 (reject →
// no scripts, allow regenerating a proposal). Design → GraphQL Schema
// (Mutation.agentGenerateCourse / approveCourseStructure,
// Query.getGenerationRequest, Query.getProject.voiceProfiles/artStyleProfiles,
// type GenerationRequest with proposedStructure) and Frontend (Agent course
// generation form); Sequence Flows (agentGenerateCourse → approveCourseStructure).

import { gql } from '@apollo/client';
import type { AwsJson, GenerationRequest } from '@courseai/domain';

// ---------------------------------------------------------------------------
// Profile selectors — Project.voiceProfiles / Project.artStyleProfiles
// ---------------------------------------------------------------------------

/**
 * The minimal Voice_Profile fields the agent form's voice selector needs: the
 * id it submits (currently informational — the agent operation does not bind a
 * voice) plus a label and the default flag so the picker can preselect the
 * project default (Req 10.6).
 */
export interface VoiceProfileOption {
  readonly voiceProfileId: string;
  readonly name: string;
  readonly isDefault: boolean;
}

/**
 * The minimal Art_Style_Profile fields the agent form's art-style selector
 * needs: id, label, and the default flag.
 */
export interface ArtStyleProfileOption {
  readonly artStyleProfileId: string;
  readonly name: string;
  readonly isDefault: boolean;
}

/** Variables for the `getProject`-scoped profile-options query. */
export interface AgentProfileOptionsVariables {
  readonly projectId: string;
}

/**
 * Shape of the profile-options query result. `getProject` is nullable
 * server-side (a non-member receives an authorization error and the field
 * resolves to null); the form treats an absent project as empty option lists.
 */
export interface AgentProfileOptionsData {
  readonly getProject: {
    readonly projectId: string;
    readonly voiceProfiles: ReadonlyArray<VoiceProfileOption>;
    readonly artStyleProfiles: ReadonlyArray<ArtStyleProfileOption>;
  } | null;
}

/**
 * Load the project's Voice_Profiles and Art_Style_Profiles as lightweight
 * options for the agent form's selectors. This is the agent flow's OWN document
 * (distinct from graphql/profiles.ts) so the form does not couple to the
 * profile-management feature; membership is enforced server-side and identity
 * comes from the JWT, so no identity is sent.
 */
export const GET_AGENT_PROFILE_OPTIONS = gql`
  query GetAgentProfileOptions($projectId: ID!) {
    getProject(projectId: $projectId) {
      projectId
      voiceProfiles {
        voiceProfileId
        name
        isDefault
      }
      artStyleProfiles {
        artStyleProfileId
        name
        isDefault
      }
    }
  }
`;

// ---------------------------------------------------------------------------
// Shared GenerationRequest selection
// ---------------------------------------------------------------------------

/**
 * The GenerationRequest fields the agent flow renders: identity, kind/status
 * (to know when the proposal is READY), the FAILED error description (Req 5.8),
 * `proposedStructure` (the agent's proposal to review/edit, Req 5.2), and the
 * course/timestamps for context.
 */
export type AgentGenerationRequest = Pick<
  GenerationRequest,
  | 'projectId'
  | 'generationRequestId'
  | 'kind'
  | 'status'
  | 'courseId'
  | 'errorDescription'
  | 'proposedStructure'
  | 'createdAt'
  | 'updatedAt'
>;

// The selection set shared by the agent mutations and the polling query so all
// three deliver the same AgentGenerationRequest shape (including the AWSJSON
// `proposedStructure` the review step reads).
const AGENT_GENERATION_REQUEST_FIELDS = `
  projectId
  generationRequestId
  kind
  status
  courseId
  errorDescription
  proposedStructure
  createdAt
  updatedAt
`;

// ---------------------------------------------------------------------------
// Submit topic prompt — agentGenerateCourse
// ---------------------------------------------------------------------------

/** Variables for `agentGenerateCourse`. Mirrors `AgentGenerateCourseInput`. */
export interface AgentGenerateCourseVariables {
  readonly input: {
    readonly projectId: string;
    readonly title: string;
    readonly topicPrompt: string;
    readonly language?: string | undefined;
  };
}

/** Shape of the `agentGenerateCourse` mutation result. */
export interface AgentGenerateCourseData {
  readonly agentGenerateCourse: AgentGenerationRequest;
}

/**
 * Submit a topic prompt so the Agent_Service researches it and proposes a
 * course structure (Req 5.1). Owner/editor only, enforced server-side. Returns
 * a GenerationRequest that starts PENDING; the agent researches and stores a
 * `proposedStructure` asynchronously, transitioning the request to READY. The
 * client polls `getGenerationRequest` (below) — or watches the Generaciones tab
 * — until the proposal is ready to review (Req 5.2).
 */
export const AGENT_GENERATE_COURSE = gql`
  mutation AgentGenerateCourse($input: AgentGenerateCourseInput!) {
    agentGenerateCourse(input: $input) {
      ${AGENT_GENERATION_REQUEST_FIELDS}
    }
  }
`;

// ---------------------------------------------------------------------------
// Poll the request — getGenerationRequest
// ---------------------------------------------------------------------------

/** Variables for the agent-flow `getGenerationRequest` poll. */
export interface GetAgentGenerationRequestVariables {
  readonly projectId: string;
  readonly generationRequestId: string;
}

/** Shape of the agent-flow `getGenerationRequest` result. Nullable server-side. */
export interface GetAgentGenerationRequestData {
  readonly getGenerationRequest: AgentGenerationRequest | null;
}

/**
 * Fetch the agent GenerationRequest by id, including `proposedStructure`. The
 * form polls this after `agentGenerateCourse` until the request reaches READY
 * (proposal ready to review) or FAILED (Req 5.2, 5.8). Membership is enforced
 * server-side; this is the agent flow's own document, keeping the same
 * selection set as the mutations above so cached entries stay consistent.
 */
export const GET_AGENT_GENERATION_REQUEST = gql`
  query GetAgentGenerationRequest($projectId: ID!, $generationRequestId: ID!) {
    getGenerationRequest(projectId: $projectId, generationRequestId: $generationRequestId) {
      ${AGENT_GENERATION_REQUEST_FIELDS}
    }
  }
`;

// ---------------------------------------------------------------------------
// Approve the (edited) structure — approveCourseStructure
// ---------------------------------------------------------------------------

/**
 * One episode in the structure submitted for approval. Mirrors
 * `EpisodeStructureInput` (episodeIndex, title, proposedDurationSeconds). The
 * list may be edited from the proposal, but its length must stay within 3–12
 * (validated client-side with `isValidEpisodeCount` before submit, and enforced
 * server-side; Req 5.3).
 */
export interface EpisodeStructureInput {
  readonly episodeIndex: number;
  readonly title: string;
  readonly proposedDurationSeconds: number;
}

/** Variables for `approveCourseStructure`. Mirrors `ApproveCourseStructureInput`. */
export interface ApproveCourseStructureVariables {
  readonly input: {
    readonly projectId: string;
    readonly generationRequestId: string;
    readonly episodes: ReadonlyArray<EpisodeStructureInput>;
  };
}

/** Shape of the `approveCourseStructure` mutation result. */
export interface ApproveCourseStructureData {
  readonly approveCourseStructure: AgentGenerationRequest;
}

/**
 * Approve a (possibly edited) proposed structure, which creates the Course +
 * Episode rows and triggers per-episode script generation (Req 5.3, 5.4).
 * Owner/editor only, enforced server-side; the server also re-checks that the
 * episode count stays within 3–12. Returns a GenerationRequest tracking the
 * script-generation work.
 */
export const APPROVE_COURSE_STRUCTURE = gql`
  mutation ApproveCourseStructure($input: ApproveCourseStructureInput!) {
    approveCourseStructure(input: $input) {
      ${AGENT_GENERATION_REQUEST_FIELDS}
    }
  }
`;

// ---------------------------------------------------------------------------
// proposedStructure shape (client-side view of the AWSJSON proposal)
// ---------------------------------------------------------------------------

/**
 * One proposed episode as read from the agent's `proposedStructure` AWSJSON.
 * The agent proposes an index, a title, and a duration for each episode (Req
 * 5.1); the review UI renders these and lets the creator edit them before
 * approval. Fields are optional/loose because the payload is free-form AWSJSON;
 * {@link parseProposedEpisodes} normalizes it into a strict editable form.
 */
export interface ProposedEpisode {
  readonly episodeIndex?: number;
  readonly title?: string;
  readonly proposedDurationSeconds?: number;
  /** The agent's one/two-sentence summary of the episode (review-only). */
  readonly summary?: string;
}

/**
 * Normalize the free-form `proposedStructure` AWSJSON into an ordered list of
 * proposed episodes for the review table. Accepts either a bare array of
 * episodes or an object with an `episodes` array (the two shapes the agent may
 * emit), tolerates missing fields, and returns entries sorted by ascending
 * `episodeIndex` so the review order matches the intended play order (Req 5.2).
 * Returns an empty list for anything it cannot interpret.
 */
export function parseProposedEpisodes(
  proposedStructure: AwsJson | null | undefined,
): ReadonlyArray<ProposedEpisode> {
  if (proposedStructure === null || proposedStructure === undefined) {
    return [];
  }

  const raw: unknown = Array.isArray(proposedStructure)
    ? proposedStructure
    : (proposedStructure as Record<string, unknown>).episodes;

  if (!Array.isArray(raw)) {
    return [];
  }

  const episodes: ProposedEpisode[] = raw
    .filter((item): item is Record<string, unknown> => typeof item === 'object' && item !== null)
    .map((item, position) => {
      const episodeIndex = typeof item.episodeIndex === 'number' ? item.episodeIndex : position;
      const title = typeof item.title === 'string' ? item.title : '';
      // The agent persists the duration as `durationSeconds`; older/edited
      // payloads may carry `proposedDurationSeconds`. Accept either.
      const proposedDurationSeconds =
        typeof item.durationSeconds === 'number'
          ? item.durationSeconds
          : typeof item.proposedDurationSeconds === 'number'
            ? item.proposedDurationSeconds
            : 0;
      const summary = typeof item.summary === 'string' ? item.summary : undefined;
      return {
        episodeIndex,
        title,
        proposedDurationSeconds,
        ...(summary !== undefined ? { summary } : {}),
      };
    });

  return [...episodes].sort((a, b) => (a.episodeIndex ?? 0) - (b.episodeIndex ?? 0));
}

/**
 * Read the agent's course-structure `overview` (its reasoning on how the course
 * is structured) from the free-form `proposedStructure` AWSJSON. Returns
 * undefined when absent (older proposals, or the model produced none).
 */
export function parseProposedOverview(
  proposedStructure: AwsJson | null | undefined,
): string | undefined {
  if (
    proposedStructure === null ||
    proposedStructure === undefined ||
    Array.isArray(proposedStructure) ||
    typeof proposedStructure !== 'object'
  ) {
    return undefined;
  }
  const overview = (proposedStructure as Record<string, unknown>).overview;
  return typeof overview === 'string' && overview.trim().length > 0 ? overview.trim() : undefined;
}
